import React from "react";
import { motion } from "framer-motion";
import flutter from '../images/flutter.png';
import firebase from '../images/firebase.png';
import node from '../images/node.webp';
import mongo from '../images/mongo.png';
import java from '../images/java.svg';
import python from '../images/python.png';
import food from '../images/food.jpg';
import Navbar from "../components/Navbar";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" }
};

export default function MobileDevelopment() {
  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/10 overflow-x-hidden font-inter">
      <Navbar />

      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 left-0 w-[800px] h-[800px] bg-blue-500/5 blur-[120px] rounded-full -ml-48 -mt-48 animate-blob" />
      <div className="fixed bottom-0 right-0 w-[600px] h-[600px] bg-indigo-500/5 blur-[120px] rounded-full -mr-32 -mb-32 animate-blob delay-2000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      {/* Hero Section */}
      <section className="relative pt-64 pb-32 px-6 flex flex-col items-center text-center overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative mb-16"
        >
          <div className="absolute -inset-10 bg-blue-500/10 blur-[130px] rounded-full" />
          <h1 className="text-5xl md:text-8xl lg:text-[11rem] font-black text-white mb-10 tracking-tighter relative z-10 uppercase leading-[0.8] font-inter px-4">
            Mobile <br /><span className="text-gradient">Engine.</span>
          </h1>
        </motion.div>

        <p className="text-lg md:text-2xl lg:text-3xl text-slate-400 max-w-4xl mb-20 leading-relaxed relative z-10 font-medium lowercase tracking-tight px-6">
          we architect high-performance binary ecosystems. native and cross-platform
          mobile infrastructure that redefine the user-device interaction loop.
        </p>

        <div className="flex gap-10 relative z-10">
          <a
            href="https://wa.me/94723954658"
            target="_blank"
            rel="noopener noreferrer"
            className="px-16 py-7 bg-slate-900 text-white font-black rounded-[2.5rem] shadow-2xl hover:bg-black transition-all hover:scale-105 active:scale-95 uppercase tracking-widest text-[11px] flex items-center gap-3"
          >
            INITIATE APP SPRINT
            <span className="text-2xl transition-transform group-hover:translate-x-1">→</span>
          </a>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-40 px-6 relative z-10 bg-[#020617] border-y border-white/5 backdrop-blur-sm">
        <div className="absolute inset-0 bg-grid opacity-5 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { icon: "📱", title: "Native & Hybrid", desc: "Forging high-performance apps using Flutter, Swift, and Kotlin for infinite reach." },
              { icon: "⚡", title: "Reactive UI", desc: "Immersive, motion-driven interfaces that feel alive on every touch and interaction." },
              { icon: "🛠️", title: "Cloud Integration", desc: "Robust real-time backends with Firebase and Node.js infrastructure for massive scale." },
              { icon: "🛡️", title: "Enterprise Security", desc: "Biometric authentication and end-to-end encrypted data streams for total integrity." },
              { icon: "📡", title: "Offline Sync", desc: "Seamless operation in low-connectivity environments with local-first operational logic." },
              { icon: "📘", title: "Full Documentation", desc: "Comprehensive technical blueprints and strategy guides for every individual build." },
            ].map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                className="group glass-card p-10 md:p-16 rounded-[3rem] md:rounded-[4.5rem] border border-white/10 hover:bg-white/5 transition-all duration-700 hover:shadow-2xl hover:translate-y-[-10px] glow-border cursor-default"
              >
                <div className="w-20 h-20 md:w-24 md:h-24 bg-slate-900/40 text-5xl md:text-6xl mb-12 flex items-center justify-center rounded-2xl md:rounded-3xl border border-white/5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-sm">
                  {service.icon}
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white mb-8 uppercase tracking-tighter leading-none">{service.title}</h3>
                <p className="text-slate-400 text-base md:text-lg font-medium leading-relaxed lowercase tracking-tight">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-40 px-6 bg-slate-900/40 relative z-10 border-b border-white/5">
        <div className="container mx-auto max-w-6xl relative z-10">
          <h2 className="text-[11px] font-black text-slate-400 mb-28 text-center uppercase tracking-[0.5em]">The Mobile Stack</h2>
          <div className="flex flex-wrap justify-center gap-20">
            {[
              { name: "Flutter", img: flutter },
              { name: "Node.js", img: node },
              { name: "Java", img: java },
              { name: "Python", img: python },
              { name: "Firebase", img: firebase },
              { name: "MongoDB", img: mongo },
            ].map((tool, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -12 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="flex flex-col items-center gap-8 group"
              >
                <div className="w-32 h-32 bg-slate-900/40 rounded-[3rem] p-8 border border-white/5 flex items-center justify-center shadow-sm group-hover:shadow-2xl group-hover:shadow-blue-500/10 transition-all duration-500">
                  <img src={tool.img} alt={tool.name} className="w-full h-full object-contain transition-all duration-700" />
                </div>
                <span className="text-[11px] font-black text-slate-400 group-hover:text-white uppercase tracking-widest leading-none transition-colors">{tool.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-40 px-6 relative z-10 bg-[#020617] overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 bg-grid opacity-5 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-32">
            <h2 className="text-5xl font-black text-white mb-6 tracking-tighter uppercase leading-none">Featured Builds</h2>
            <p className="text-slate-400 font-medium lowercase tracking-tight max-w-sm mx-auto">High-performance mobile ecosystems delivered to global enterprises.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-12 max-w-7xl mx-auto">
            {[
              {
                title: "FoodFlowSL",
                img: food,
                tag: "Restaurant App",
                desc: "Integrated POS and delivery management system for modern restaurants."
              }
            ].map((project, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative bg-slate-900/40 rounded-[3rem] overflow-hidden border border-white/5 hover:shadow-2xl transition-all duration-700 w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)]"
              >
                  <div className="relative h-64 overflow-hidden bg-[#020617]">
                    <img src={project.img} alt={project.title} className="w-full h-full object-contain group-hover:scale-105 transition-all duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent opacity-40 pointer-events-none" />
                    <span className="absolute top-6 left-6 px-4 py-2 bg-white/5 backdrop-blur-md rounded-lg text-[9px] font-black text-white uppercase tracking-widest border border-white/10 shadow-2xl">
                      {project.tag}
                    </span>
                  </div>
                  <div className="p-10">
                    <h3 className="text-2xl font-black text-white mb-4 tracking-tighter uppercase leading-none">{project.title}</h3>
                    <p className="text-slate-400 text-sm font-medium lowercase tracking-tight">{project.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

      {/* Final CTA */}
      <section className="py-48 px-6 relative z-10 bg-[#020617]">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-24 md:p-32 rounded-[5rem] text-center border border-white/10 shadow-[0_60px_120px_rgba(0,0,0,0.5)] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 blur-[100px] rounded-full -mr-32 -mt-32" />
            <h2 className="text-5xl md:text-7xl lg:text-[9.5rem] font-black text-white mb-14 leading-[0.85] tracking-tighter uppercase relative z-10 px-4">Home <br /><span className="text-gradient">Screen.</span></h2>
            <div className="flex justify-center gap-10 relative z-10">
              <a
                href="https://wa.me/94723954658"
                className="px-16 py-7 bg-blue-600 text-white font-black rounded-[2.5rem] hover:bg-blue-700 transition-all shadow-[0_25px_50px_rgba(37,99,235,0.25)] hover:scale-105 active:scale-95 uppercase tracking-widest text-xs"
              >
                START APP SPRINT
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="py-24 px-6 bg-slate-900/40 border-t border-white/5 text-center">
        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.5em]">© 2024 LAB Developers — Mobile Engineering Excellence.</p>
      </footer>
    </div>
  );
}
